/**
 * Generic Parameter Logger Utility
 *
 * A consolidated utility for logging parameter changes in widget components.
 * Eliminates code duplication between settings and style parameter loggers.
 */
import { useEffect, useRef, useMemo } from 'react';

// Environment-based logging control
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

interface ParamsObject {
  [key: string]: any;
}

interface ParamLoggerConfig {
  /** Environment variable name to check (e.g., 'SETTINGS_LOGGER_ENABLED') */
  envVarName: string;
  /** Emoji prefix for log messages (e.g., '⚙️' or '🎨') */
  logEmoji: string;
  /** Parameter type name for error messages (e.g., 'settings' or 'style') */
  paramTypeName: string;
}

/**
 * Check if logging is enabled based on environment variable
 * Falls back to development mode if not explicitly set
 */
const getLoggerEnabled = (envVarName: string): boolean => {
  const envValue = process.env[envVarName];

  if (envValue !== undefined) {
    // Explicitly set in environment - respect the setting
    return envValue.toLowerCase() === 'true';
  }

  // Not explicitly set - default to development mode
  return IS_DEVELOPMENT;
};

/**
 * Automatically extracts all parameter names from a params object
 */
const getAllParamNames = (paramsObject: ParamsObject): string[] => {
  return Object.keys(paramsObject).filter((key) => key !== 'default');
};

/**
 * Generic hook for logging parameter changes in widget components
 *
 * @param paramsObject - The params object (e.g., settingsParams or stylesParams)
 * @param getter - Function to get parameter values (e.g., settings.get or styles.get)
 * @param config - Configuration for logging behavior
 */
export const useParamLogger = (
  paramsObject: ParamsObject,
  getter: (param: any) => any,
  config: ParamLoggerConfig,
) => {
  const shouldLog = useMemo(
    () => getLoggerEnabled(config.envVarName),
    [config.envVarName],
  );

  // Get all parameter names automatically from the params object
  const allParamNames = useMemo(() => {
    if (!shouldLog) {
      return [];
    }
    return getAllParamNames(paramsObject);
  }, [paramsObject, shouldLog]);

  // Get current values for all parameters
  const currentValues = useMemo(() => {
    if (!shouldLog || allParamNames.length === 0) {
      return {};
    }
    return allParamNames.reduce((acc, paramName) => {
      try {
        acc[paramName] = getter(paramsObject[paramName]);
      } catch (error) {
        // Skip parameters that might not exist or are invalid
        console.warn(
          `⚠️ Could not get ${config.paramTypeName} parameter: ${paramName}`,
          error,
        );
      }
      return acc;
    }, {} as Record<string, any>);
  }, [getter, allParamNames, paramsObject, shouldLog, config.paramTypeName]);

  // Store previous values for comparison
  const previousValues = useRef(currentValues);

  useEffect(() => {
    if (!shouldLog || allParamNames.length === 0) {
      return;
    }

    // Check each parameter for changes and log them
    allParamNames.forEach((paramName) => {
      const previousValue = previousValues.current[paramName];
      const currentValue = currentValues[paramName];

      if (JSON.stringify(previousValue) !== JSON.stringify(currentValue)) {
        console.log(
          `${config.logEmoji} ${paramName}: ${JSON.stringify(
            previousValue,
          )} → ${JSON.stringify(currentValue)}`,
        );
      }
    });

    // Update previous values for next comparison
    previousValues.current = { ...currentValues };
  }, [currentValues, allParamNames, shouldLog, config.logEmoji]);
};
